import { useApiAction } from "api/drops/useApiAction";
import {
  agentPauseTelephoneApi,
  pauseTypesGetTelephoneApi,
  userGetTelephoneApi,
  userLogOnOffTelephoneApi,
} from "api/puzzel/puzzel";
import constate from "constate";
import {
  PauseType,
  PresenceStatus,
} from "features/Header/PuzzelTelephone/components/types";
import { usePuzzelAuthenticationContext } from "features/Header/PuzzelTelephone/provider/contexts/PuzzelAuthenticationContext";
import { useUserValidationState } from "features/Header/PuzzelTelephone/provider/contexts/UserValidationContext";
import { useEffect, useState } from "react";

// Maps the CC5 "LoggedOff" | "LoggedOn" | "Pause" result to our own status - exported so
// Logon.tsx can apply the same mapping to the live-polled extended state information.
export const mapResultToPresenceStatus = (result: string): PresenceStatus => {
  if (result === "LoggedOff") return "loggedOff";
  if (result === "Pause") return "paused";
  return "available";
};

function useCallStatus() {
  const { userId } = useUserValidationState();

  const { isAuthenticated } = usePuzzelAuthenticationContext();

  const [userGetRequest, performUserGet]: any = useApiAction(
    userGetTelephoneApi,
    false,
  );

  const [
    userLogOnOffRequest,
    performUserLogOnOffRaw,
    resetUserLogOnOffRequest,
  ] = useApiAction(userLogOnOffTelephoneApi, false);

  const [pauseRequest, performAgentPauseRaw, resetPauseRequest] = useApiAction(
    agentPauseTelephoneApi,
    false,
    false,
  );

  const [pauseTypesRequest, performPauseTypesGet]: any = useApiAction(
    pauseTypesGetTelephoneApi,
    false,
  );

  useEffect(() => {
    if (isAuthenticated && userId) {
      performUserGet({ userId });
    }
  }, [isAuthenticated, userLogOnOffRequest.data, pauseRequest.data, userId]);

  const profileId = userGetRequest.data?.result?.currentProfileId;
  const currentProfile = profileId
    ? userGetRequest.data?.result?.profiles.find(
        (profile: { id: number }) => profile.id === profileId,
      )
    : null;
  const profiles = userGetRequest.data?.result?.profiles;

  // Whichever action ran most recently wins (the other is reset to null when one is performed).
  const statusFromPauseRequest = pauseRequest.data?.result
    ? mapResultToPresenceStatus(pauseRequest.data.result)
    : undefined;
  const statusFromLogOnOffRequest = userLogOnOffRequest.data?.result
    ? mapResultToPresenceStatus(userLogOnOffRequest.data.result)
    : undefined;
  const statusFromUserDetailsRequest = userGetRequest.data?.result
    ?.contactCentreStatus
    ? mapResultToPresenceStatus(userGetRequest.data.result.contactCentreStatus)
    : undefined;

  let presenceStatus: PresenceStatus | null = null;
  if (statusFromPauseRequest !== undefined) {
    presenceStatus = statusFromPauseRequest;
  } else if (statusFromLogOnOffRequest !== undefined) {
    presenceStatus = statusFromLogOnOffRequest;
  } else if (statusFromUserDetailsRequest !== undefined) {
    presenceStatus = statusFromUserDetailsRequest;
  }

  const isLoggedOn =
    presenceStatus === null ? null : presenceStatus !== "loggedOff";

  // True while a pause/unpause request is in flight - disables the status dropdown (Logon.tsx)
  // so it can't be clicked again mid-request.
  const isPresencePending = pauseRequest.loading;

  // Bumped whenever a pause/unpause request resolves, so ListenForCallStatusContext can refetch
  // the live poll immediately instead of waiting up to 2s for its next scheduled tick.
  const [lastActionAt, setLastActionAt] = useState(0);
  useEffect(() => {
    if (pauseRequest.data) {
      setLastActionAt(Date.now());
    }
  }, [pauseRequest.data]);

  // Logon/logoff and pause/unpause are mutually exclusive actions - reset the other's cached
  // result so a stale response can't win the priority order above after the real latest action.
  const performUserLogOnOff = (args: any) => {
    resetPauseRequest();
    performUserLogOnOffRaw(args);
  };

  // pauseTypeId is optional - confirmed via native Puzzel Agent's own network traffic, which
  // sends an empty body ({}) for a generic Pause with no specific reason selected.
  const performAgentPauseOn = (pauseTypeId?: number) => {
    resetUserLogOnOffRequest();
    performAgentPauseRaw({
      param: { userId, pauseOnOrOff: "pauseon" },
      payload: pauseTypeId === undefined ? {} : { pauseTypeId },
    });
  };

  const performAgentPauseOff = () => {
    resetUserLogOnOffRequest();
    performAgentPauseRaw({
      param: { userId, pauseOnOrOff: "pauseoff" },
      payload: {},
    });
  };

  // Pause types are only meaningful once logged on, and don't change during a session.
  useEffect(() => {
    if (isAuthenticated && userId && isLoggedOn && !pauseTypesRequest.data) {
      performPauseTypesGet({ userId });
    }
  }, [isAuthenticated, userId, isLoggedOn]);

  // Shown when the account has no pause types configured in Puzzel - selecting it sends pauseon
  // with an empty body (no pauseTypeId), matching native Puzzel Agent's own generic Pause.
  const DEFAULT_PAUSE_TYPES: PauseType[] = [{ id: 0, description: "Pause" }];

  const fetchedPauseTypes: PauseType[] = pauseTypesRequest.data?.result ?? [];
  const hasRealPauseTypes = fetchedPauseTypes.length > 0;
  const pauseTypes: PauseType[] = hasRealPauseTypes
    ? fetchedPauseTypes
    : DEFAULT_PAUSE_TYPES;

  return {
    userDetails: {
      currentProfile,
      profiles,
      isLoggedOn,
      presenceStatus,
      isPresencePending,
      lastActionAt,
      pauseTypes,
      hasRealPauseTypes,
    },
    performUserLogOnOff,
    performAgentPauseOn,
    performAgentPauseOff,
  };
}

const [
  UserDetailsAndLogOnOffProvider,
  useUserDetailsState,
  usePerformUserLogOnOff,
  usePerformAgentPauseOn,
  usePerformAgentPauseOff,
] = constate(
  useCallStatus,
  (value) => value.userDetails,
  (value) => value.performUserLogOnOff,
  (value) => value.performAgentPauseOn,
  (value) => value.performAgentPauseOff,
);
export {
  usePerformAgentPauseOff,
  usePerformAgentPauseOn,
  usePerformUserLogOnOff,
  UserDetailsAndLogOnOffProvider,
  useUserDetailsState,
};
